// 消息记录导出的接口（docs/04 第 8.1 节，决策 D202）：建任务、列表与进度、删除，完成后直接下载文件。
import { useQuery } from "@tanstack/react-query";
import type { Tone } from "@/ui";
import { MESSAGE_STATE, formatTime, type MessageState } from "@/ui";
import { api } from "./api";
import { channels } from "./master";
import { messageParams, type MessageFilter } from "./messages";
import { nameOf, useList, useNames } from "./queries";
import type { ListCount } from "./usePage";

/** 任务状态：排队、导出中、完成、失败。 */
export type ExportStatus = "queued" | "running" | "completed" | "failed";

/** 任务状态的胶囊。 */
export const EXPORT_STATUS: Record<ExportStatus, { tone: Tone; label: string }> = {
  queued: { tone: "neutral", label: "排队中" }, running: { tone: "brand", label: "导出中" }, completed: { tone: "success", label: "已完成" }, failed: { tone: "danger", label: "失败" },
};

/** 保存的筛选条件，键同消息列表的参数，空的不出现。 */
export interface ExportFilter {
  message_id?: string; msisdn?: string; account_id?: number; customer_id?: number; channel_id?: number; final_channel_id?: number;
  min_attempts?: number; mcc?: string; mnc?: string; status?: MessageState;
}

/** 一个导出任务；progress 是 0 到 1，按游标走过的时间比例估算，完成为 1。 */
export interface MessageExport {
  id: number; status: ExportStatus; fields: string[]; filter: ExportFilter; from: string; to: string; rows: number; truncated: boolean;
  file_size: number; error: string | null; operator: string; created_at: string; finished_at: string | null; progress: number;
}

/** 字段目录：fields 按导出文件的列序，defaults 是缺省勾选的键。 */
export interface ExportFields { fields: { key: string; label: string }[]; defaults: string[] }

/** 未完成的任务：列表要轮询进度。 */
export const exportOpen = (e: Pick<MessageExport, "status">) => e.status === "queued" || e.status === "running";

export const messageExports = {
  fields: () => api<ExportFields>("/message-exports/fields"),
  /** 按消息列表的条件（查询串）与选中的字段建任务；未完成的已有 10 个时 409。 */
  create: (f: MessageFilter, fields: string[]) => api<MessageExport>(`/message-exports?${messageParams(f)}`, { method: "POST", body: JSON.stringify({ fields }) }),
  list: (limit: number) => api<MessageExport[]>(`/message-exports?limit=${limit}`),
  count: () => api<ListCount>("/message-exports/count"),
  remove: (id: number) => api<{ deleted: boolean }>(`/message-exports/${id}`, { method: "DELETE" }),
  /** 文件地址：浏览器直接下载，大文件不经过页面内存。 */
  fileURL: (id: number) => `/api/message-exports/${id}/file`,
};

/** 取 ID 对应名称的函数，同 useNames。 */
export interface NameOf { account: (id: number) => string; customer: (id: number) => string; channel: (id: number) => string }

/** 条件摘要用的名称：账号与客户同 useNames（含系统测试账号），通道取通道列表。 */
export function useExportNames(): NameOf {
  const names = useNames();
  const channelList = useList("channels", channels);
  return { ...names, channel: (id: number) => nameOf(channelList.data, id) };
}

/** 筛选条件的摘要，一项一句，例如“账号：otp”；条件的写法同消息记录页的筛选栏。 */
export function filterSummary(f: ExportFilter, names: NameOf): string[] {
  const out: string[] = [];
  const add = (label: string, v: string | number | undefined, show: (v: never) => string = String) => { if (v !== undefined && v !== "") out.push(`${label}：${show(v as never)}`); };
  add("message_id", f.message_id);
  add("号码", f.msisdn);
  add("MCC", f.mcc);
  add("MNC", f.mnc);
  add("最少投递次数", f.min_attempts);
  add("客户", f.customer_id, names.customer);
  add("账号", f.account_id, names.account);
  add("通道", f.channel_id, names.channel);
  add("最终通道", f.final_channel_id, names.channel);
  add("状态", f.status, (s: MessageState) => MESSAGE_STATE[s]?.label ?? s);
  return out;
}

/** 消息页的筛选条件（查询串的字符串值）换成保存的条件形态，供建任务前显示摘要。 */
export function exportFilterOf(f: MessageFilter): ExportFilter {
  const num = (v?: string) => (v ? Number(v) : undefined);
  return {
    message_id: f.message_id || undefined, msisdn: f.msisdn || undefined, account_id: num(f.account_id), customer_id: num(f.customer_id), channel_id: num(f.channel_id),
    final_channel_id: num(f.final_channel_id), min_attempts: num(f.min_attempts), mcc: f.mcc || undefined, mnc: f.mnc || undefined, status: (f.status || undefined) as MessageState | undefined,
  };
}

/** 宽条件导出的最长范围，与服务端 msgexport.WideMaxRange 一致；宽条件的口径同 pg.MessageQuery.Wide（决策 D82、D202）。 */
const WIDE_MAX_MS = 7 * 24 * 3600 * 1000;

/** 没有号码、账号与 message_id 的宽条件，且范围超过 7 天：服务端会拒绝，表单里提前提示。 */
export function exportTooWide(f: MessageFilter) {
  const wide = !f.message_id && !f.msisdn && !f.account_id;
  return wide && !!f.from && !!f.to && Date.parse(f.to) - Date.parse(f.from) > WIDE_MAX_MS;
}

/** 时间范围的写法：北京时间到分钟。 */
export function rangeLabel(from: string, to: string) {
  return `${formatTime(from, false)} 至 ${formatTime(to, false)}`;
}

/** 文件大小：KB 或 MB，一位小数。 */
export function formatSize(bytes: number) {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(0.1, bytes / 1024).toFixed(1)} KB`;
}

/** 导出任务抽屉列出的任务数：任务保留 7 天，最近的这些够用，更多时写明总数。 */
export const EXPORTS_SHOWN = 50;
/** 有未完成的任务时刷新进度的间隔。 */
const POLL_MS = 2000;

/** 导出任务列表与进度：有未完成的任务时每 2 秒刷新（决策 D202）。顶栏任务中心的按钮与抽屉共用。 */
export function useMessageExports() {
  return useQuery({ queryKey: ["message-exports", "list"], queryFn: () => messageExports.list(EXPORTS_SHOWN), refetchInterval: (q) => (q.state.data?.some(exportOpen) ? POLL_MS : false) });
}
